const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_SUPABASE_ANON_KEY;

const STORAGE_KEY = 'cm_supabase_session_v1';

function hasConfig() {
  return Boolean(SUPABASE_URL && SUPABASE_KEY);
}

function saveSession(session) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

function loadSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearSession() {
  localStorage.removeItem(STORAGE_KEY);
}

function parseParams(value) {
  const out = {};
  const raw = String(value || '').replace(/^[#?]/, '');
  if (!raw) return out;
  const params = new URLSearchParams(raw);
  params.forEach((v, k) => {
    out[k] = v;
  });
  return out;
}

function parseAllUrlAuthParams() {
  const hashRaw = String(window.location.hash || '');
  const searchRaw = String(window.location.search || '');

  const hashParts = hashRaw.split('?');
  const directHashParams = parseParams(hashParts.length > 1 ? hashParts.slice(1).join('?') : '');
  const hashTokenParams = parseParams(hashRaw.startsWith('#access_token=') ? hashRaw : '');
  const searchParams = parseParams(searchRaw);

  return {
    ...searchParams,
    ...directHashParams,
    ...hashTokenParams
  };
}

export function getStoredSession() {
  return loadSession();
}

export function isSupabaseAuthConfigured() {
  return hasConfig();
}

export async function loginWithEmailPassword(email, password) {
  if (!hasConfig()) {
    throw new Error('Supabase is not configured');
  }

  const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_KEY,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email, password })
  });

  const text = await response.text();
  if (!response.ok) {
    throw new Error(text || 'Login failed');
  }

  const data = JSON.parse(text);
  const session = {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    token_type: data.token_type,
    expires_in: data.expires_in,
    user: data.user
  };
  saveSession(session);
  return session;
}

export async function signUpWithEmailPassword(email, password, userMetadata = {}) {
  if (!hasConfig()) {
    throw new Error('Supabase is not configured');
  }

  const response = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_KEY,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      email,
      password,
      data: userMetadata && typeof userMetadata === 'object' ? userMetadata : {}
    })
  });

  const text = await response.text();
  if (!response.ok) {
    throw new Error(text || 'Signup failed');
  }

  const data = JSON.parse(text || '{}');
  // Depending on Supabase settings, signup may return a session immediately
  // (email confirmations disabled) or only a user (confirmations enabled).
  if (data?.access_token && data?.user) {
    const session = {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      token_type: data.token_type,
      expires_in: data.expires_in,
      user: data.user
    };
    saveSession(session);
    return { session, needsEmailConfirmation: false };
  }

  return { session: null, needsEmailConfirmation: true };
}

export function logout() {
  clearSession();
}

export function getAccessToken() {
  const session = loadSession();
  return session?.access_token || '';
}

export async function refreshSession() {
  if (!hasConfig()) throw new Error('Supabase is not configured');
  const session = loadSession();
  const refreshToken = session?.refresh_token;
  if (!refreshToken) throw new Error('No refresh token');

  const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_KEY,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ refresh_token: refreshToken })
  });

  const text = await response.text();
  if (!response.ok) throw new Error(text || 'Token refresh failed');

  const data = JSON.parse(text);
  const next = {
    access_token: data.access_token,
    refresh_token: data.refresh_token || refreshToken,
    token_type: data.token_type,
    expires_in: data.expires_in,
    user: data.user || session?.user
  };
  saveSession(next);
  return next;
}

export function getUser() {
  const session = loadSession();
  return session?.user || null;
}

export async function sendPasswordResetEmail(email) {
  if (!hasConfig()) throw new Error('Supabase is not configured');
  const safeEmail = String(email || '').trim();
  if (!safeEmail) throw new Error('Missing email');

  const response = await fetch(`${SUPABASE_URL}/auth/v1/recover`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_KEY,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      email: safeEmail,
      options: {
        redirectTo: `${window.location.origin}/#/login`
      }
    })
  });

  const text = await response.text();
  if (!response.ok) throw new Error(text || 'Password reset request failed');
  return true;
}

export function hasRecoveryParamsInUrl() {
  const p = parseAllUrlAuthParams();
  return String(p.type || '').toLowerCase() === 'recovery' && Boolean(String(p.access_token || '').trim());
}

export function consumeRecoverySessionFromUrl() {
  if (!hasConfig()) return false;
  const p = parseAllUrlAuthParams();
  const type = String(p.type || '').toLowerCase();
  const accessToken = String(p.access_token || '').trim();
  if (type !== 'recovery' || !accessToken) return false;

  const refreshToken = String(p.refresh_token || '').trim();
  const tokenType = String(p.token_type || 'bearer').trim();
  const expiresIn = Number(p.expires_in || 3600) || 3600;

  saveSession({
    access_token: accessToken,
    refresh_token: refreshToken,
    token_type: tokenType,
    expires_in: expiresIn,
    user: loadSession()?.user || null
  });
  return true;
}

async function updatePasswordRequest(accessToken, password) {
  const response = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
    method: 'PUT',
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ password: String(password || '') })
  });
  return response;
}

export async function changeMyPassword(password) {
  if (!hasConfig()) throw new Error('Supabase is not configured');
  const nextPassword = String(password || '');
  if (!nextPassword) throw new Error('Missing password');

  let accessToken = getAccessToken();
  if (!accessToken) throw new Error('Missing access token');

  let response = await updatePasswordRequest(accessToken, nextPassword);
  if (response.status === 401 || response.status === 403) {
    const refreshed = await refreshSession();
    accessToken = String(refreshed?.access_token || '');
    if (!accessToken) throw new Error('Token refresh failed');
    response = await updatePasswordRequest(accessToken, nextPassword);
  }

  const text = await response.text();
  if (!response.ok) throw new Error(text || 'Password change failed');
  return true;
}
